<template>
  <div class="reports-page">
    <header class="reports-hero">
      <div>
        <span class="reports-hero__eyebrow">Circuit Crafters Intelligence</span>
        <h1>Visão 360° da operação</h1>
        <p>Indicadores de assistência técnica, SLA, peças, margem e capacidade da equipa.</p>
      </div>
      <div class="reports-hero__period">
        <i class="pi pi-calendar"></i>
        <span>{{ periodLabel }}</span>
      </div>
    </header>

    <ReportFilterBar
      :options="filterOptions"
      :value="filters"
      :scope="filterScope"
      @apply="applyFilters"
      @reset="resetFilters"
    />

    <Message v-if="error" severity="error" :closable="true" @close="error = ''">
      {{ error }}
    </Message>
    <Message v-for="(message, key) in currentErrors" :key="key" severity="error" :closable="false">
      {{ message }} <Button label="Tentar novamente" class="p-button-text" @click="loadTab(activeTabId, true)" />
    </Message>

    <TabView v-if="visibleTabs.length" :activeIndex.sync="activeTab" @tab-change="onTabChange" class="reports-tabs">
      <TabPanel v-if="canOperations" header="Visão Geral">
        <div class="reports-kpis">
          <KpiCard label="PATs no período" :value="number(summary.totalPats)" icon="pi pi-inbox" />
          <KpiCard label="PATs em aberto" :value="number(summary.openPats)" icon="pi pi-clock" tone="amber" />
          <KpiCard label="Taxa de conclusão" :value="percent(summary.closureRate)" icon="pi pi-check-circle" tone="green" />
          <KpiCard label="Tempo médio" :value="days(summary.avgResolutionDays)" icon="pi pi-stopwatch" tone="blue" />
          <KpiCard label="Cumprimento SLA" :value="percent(summary.slaComplianceRate)" :detail="`Meta: ${summary.slaDays || filters.slaDays} dia(s)`" icon="pi pi-bolt" tone="purple" />
          <KpiCard v-if="canFinance" label="Valor documental (provisório)" :value="money(financialSummary.revenue)" :detail="`${number(financialSummary.revenueDocuments)} documentos`" icon="pi pi-euro" tone="green" />
          <KpiCard v-if="canFinance" label="Custos de peças (provisórios)" :value="money(financialSummary.partsCost)" :detail="`${number(financialSummary.partsQuantity)} unidades`" icon="pi pi-cog" tone="red" />
          <KpiCard v-if="canFinance" label="Margem estimada" :value="money(financialSummary.estimatedMargin)" :detail="percent(financialSummary.estimatedMarginRate)" icon="pi pi-chart-line" tone="teal" />
        </div>
        <p class="reports-scope-note">PATs por data de abertura; SLA e tempo médio por data de fecho válida. SLA em dias de calendário, não NBD contratual.</p>
        <p v-if="canFinance" class="reports-scope-note">Valores financeiros provisórios, apenas por data, sujeitos a validação PHC/Financeira.</p>

        <div class="reports-grid reports-grid--thirds">
          <EchartCard title="Cumprimento SLA" subtitle="PATs resolvidos dentro do limite" :option="slaGaugeOption" :loading="widgetLoading.sla" :error="widgetErrors.sla" :empty="!sla.closedPats" />
          <EchartCard title="Pipeline operacional" subtitle="Volume por estado" :option="pipelineOption" :loading="widgetLoading.pipeline" :error="widgetErrors.pipeline" />
          <EchartCard title="Tipos de serviço" subtitle="Distribuição dos pedidos" :option="serviceTypesOption" :loading="widgetLoading.serviceTypes" :error="widgetErrors.serviceTypes" />
        </div>
        <div class="reports-grid reports-grid--full">
          <EchartCard title="Evolução da atividade" subtitle="PATs abertos, fechados e intervenções" :option="trendOption" :loading="widgetLoading.trend" :error="widgetErrors.trend" tall />
        </div>
        <div class="reports-grid">
          <EchartCard title="Desempenho por marca" subtitle="PATs abertos vs. concluídos" :option="brandsOption" :loading="widgetLoading.brands" :error="widgetErrors.brands" />
          <EchartCard title="Clientes com maior atividade" subtitle="Ordenado por número de PATs" :option="clientsOption" :loading="widgetLoading.clients" :error="widgetErrors.clients" />
        </div>
      </TabPanel>

      <TabPanel v-if="canOperations" header="Operação & SLA">
        <div class="reports-kpis reports-kpis--compact">
          <KpiCard label="PATs fechados" :value="number(sla.closedPats)" icon="pi pi-check" />
          <KpiCard label="Dentro do SLA" :value="number(sla.withinSla)" icon="pi pi-bolt" tone="green" />
          <KpiCard label="Cumprimento" :value="percent(sla.complianceRate)" icon="pi pi-chart-pie" tone="purple" />
          <KpiCard label="MTTR" :value="days(sla.avgResolutionDays)" icon="pi pi-stopwatch" tone="blue" />
        </div>
        <div class="reports-grid reports-grid--thirds">
          <EchartCard title="SLA (dias de calendário)" subtitle="PATs por data de fecho válida; não equivale a NBD" :option="slaGaugeOption" :loading="widgetLoading.sla" :error="widgetErrors.sla" :empty="!sla.closedPats" />
          <EchartCard title="Tempo até resolução" subtitle="PATs fechados por intervalo" :option="slaBucketsOption" :loading="widgetLoading.sla" :error="widgetErrors.sla" />
          <EchartCard title="MTTR mensal" subtitle="Dias médios até conclusão" :option="slaMonthlyOption" :loading="widgetLoading.sla" :error="widgetErrors.sla" :empty="!sla.closedPats" />
        </div>
        <div class="reports-grid">
          <EchartCard title="Tipos de intervenção" subtitle="Contacto e trabalho técnico" :option="interventionTypesOption" :loading="widgetLoading.interventionTypes" :error="widgetErrors.interventionTypes" />
          <EchartCard title="Resoluções por técnico" subtitle="Concluídos vs. em aberto" :option="techniciansOption" :loading="widgetLoading.technicians" :error="widgetErrors.technicians" />
        </div>
        <section class="reports-table-card">
          <div class="reports-section-heading"><h3>Duração das intervenções</h3><p>Apenas horários válidos no mesmo dia; horários invertidos ou noturnos excluídos até validação.</p></div>
          <DataTable v-if="!widgetErrors.interventionTypes" :value="interventionTypes.items || []" :loading="widgetLoading.interventionTypes" responsiveLayout="scroll">
            <Column field="name" header="Tipo" />
            <Column field="value" header="Intervenções" />
            <Column field="avgMinutes" header="Média (min)"><template #body="slotProps">{{ number(slotProps.data.avgMinutes) }}</template></Column>
            <Column field="validDurations" header="Durações válidas" />
            <Column field="durationCoverageRate" header="Cobertura"><template #body="slotProps">{{ percent(slotProps.data.durationCoverageRate) }}</template></Column>
          </DataTable>
          <p v-else role="alert">{{ widgetErrors.interventionTypes }}</p>
        </section>
        <section class="reports-table-card">
          <div class="reports-section-heading"><div><h3>Leaderboard técnico</h3><p>PATs por data de abertura; intervenções por data do registo. Horas apenas com duração válida.</p></div></div>
          <DataTable v-if="!widgetErrors.technicians" :value="technicianRows" :loading="widgetLoading.technicians" :paginator="true" :rows="10" responsiveLayout="scroll" sortField="resolved" :sortOrder="-1">
            <Column field="technician" header="Técnico" sortable />
            <Column field="assigned" header="Atribuídos" sortable />
            <Column field="open" header="Abertos" sortable />
            <Column field="resolved" header="Resolvidos" sortable />
            <Column field="avgResolutionDays" header="MTTR (dias)" sortable><template #body="slotProps">{{ number(slotProps.data.avgResolutionDays) }}</template></Column>
            <Column field="interventions" header="Intervenções" sortable />
            <Column field="workedHours" header="Horas registadas" sortable><template #body="slotProps">{{ number(slotProps.data.workedHours) }}</template></Column>
            <Column field="validDurations" header="Durações válidas" sortable />
            <Column field="durationCoverageRate" header="Cobertura"><template #body="slotProps">{{ percent(slotProps.data.durationCoverageRate) }}</template></Column>
          </DataTable>
          <p v-else role="alert">{{ widgetErrors.technicians }}</p>
        </section>
      </TabPanel>

      <TabPanel v-if="canOperations" header="Stock & Peças">
        <div class="reports-kpis reports-kpis--compact">
          <KpiCard label="Referências" :value="number(stock.kpis && stock.kpis.references)" icon="pi pi-list" />
          <KpiCard label="Valor de stock" :value="money(stock.kpis && stock.kpis.stockCostValue)" icon="pi pi-euro" tone="blue" />
          <KpiCard label="Sem stock" :value="number(stock.kpis && stock.kpis.outOfStock)" icon="pi pi-exclamation-triangle" tone="red" />
          <KpiCard label="Abaixo do mínimo" :value="number(stock.kpis && stock.kpis.belowReorderPoint)" icon="pi pi-arrow-down" tone="amber" />
        </div>
        <p class="reports-scope-note">Peças por data e técnico. Stock e parque de equipamentos são fotografias atuais e não usam os filtros.</p>
        <div class="reports-grid">
          <EchartCard title="Peças mais utilizadas" subtitle="Linhas positivas dos dossiers 5/12; consumo por validar" :option="partsOption" :loading="widgetLoading.parts" :error="widgetErrors.parts" />
          <EchartCard title="Stock por família" subtitle="Valor e unidades disponíveis" :option="stockFamiliesOption" :loading="widgetLoading.stock" :error="widgetErrors.stock" info="O stock representa uma fotografia atual e não é afetado pelo intervalo de datas." />
        </div>
        <div class="reports-grid reports-grid--thirds">
          <EchartCard title="Marcas no parque" subtitle="Equipamentos registados" :option="equipmentBrandsOption" :loading="widgetLoading.equipments" :error="widgetErrors.equipments" />
          <EchartCard title="Cobertura de garantia" :subtitle="`${number(equipments.totalEquipments)} equipamentos`" :option="warrantyOption" :loading="widgetLoading.equipments" :error="widgetErrors.equipments" />
          <section class="reports-table-card reports-table-card--chart-height">
            <div class="reports-section-heading"><div><h3>Stock crítico</h3><p>Referências abaixo do ponto de encomenda</p></div></div>
            <DataTable v-if="!widgetErrors.stock" :value="criticalRows" :loading="widgetLoading.stock" responsiveLayout="scroll" :scrollable="true" scrollHeight="270px" emptyMessage="Sem referências críticas">
              <Column field="reference" header="Referência" />
              <Column field="designation" header="Designação" />
              <Column field="stock" header="Stock" />
              <Column field="reorderPoint" header="Mínimo" />
            </DataTable>
            <p v-else role="alert">{{ widgetErrors.stock }}</p>
          </section>
        </div>
        <section class="reports-table-card">
          <div class="reports-section-heading"><div><h3>Movimentos de peças</h3><p>Linhas positivas dos dossiers 5/12; consumo e custos por validar</p></div></div>
          <DataTable v-if="!widgetErrors.parts" :value="partRows" :loading="widgetLoading.parts" :paginator="true" :rows="10" responsiveLayout="scroll">
            <Column field="reference" header="Referência" sortable />
            <Column field="designation" header="Designação" />
            <Column field="family" header="Família" sortable />
            <Column field="quantity" header="Quantidade" sortable />
            <Column field="cost" header="Custo" sortable><template #body="slotProps">{{ money(slotProps.data.cost) }}</template></Column>
          </DataTable>
          <p v-else role="alert">{{ widgetErrors.parts }}</p>
        </section>
      </TabPanel>

      <TabPanel v-if="canFinance" header="Financeiro & Contratos">
        <Message severity="warn" :closable="false">Valores provisórios: etotal dos dossiers 14/15/17, peças dos dossiers 5/12 e u_valor das intervenções. Não representam faturação ou margem contabilística validadas. Apenas o intervalo de datas é aplicado.</Message>
        <div class="reports-kpis reports-kpis--compact">
          <KpiCard label="Valor documental" :value="money(financialSummary.revenue)" icon="pi pi-euro" tone="green" />
          <KpiCard label="Custos diretos" :value="money(directCosts)" icon="pi pi-wallet" tone="red" />
          <KpiCard label="Margem estimada" :value="money(financialSummary.estimatedMargin)" :detail="percent(financialSummary.estimatedMarginRate)" icon="pi pi-chart-line" />
          <KpiCard label="Valor dos contratos" :value="money(contracts.kpis && contracts.kpis.revenue)" :detail="`${number(contracts.kpis && contracts.kpis.clients)} clientes`" icon="pi pi-briefcase" tone="purple" />
        </div>
        <div class="reports-grid reports-grid--full">
          <EchartCard title="Valores documentais, custos e margem estimada" subtitle="Evolução mensal provisória em euros" :option="financialOption" :loading="widgetLoading.financial" :error="widgetErrors.financial" tall info="A informação financeira é filtrada por data; os restantes filtros operacionais não se aplicam aos dossiers PHC." />
        </div>
        <div class="reports-grid">
          <EchartCard title="Valor por linha documental" subtitle="Dossiers 14/15/17; valores provisórios" :option="serviceLinesOption" :loading="widgetLoading.serviceLines" :error="widgetErrors.serviceLines" />
          <EchartCard title="Contratos corporate" subtitle="Volume e valor documental mensal" :option="contractsOption" :loading="widgetLoading.contracts" :error="widgetErrors.contracts" />
        </div>
        <section class="reports-table-card">
          <div class="reports-section-heading"><div><h3>Linhas de serviço PHC</h3><p>Volume, encerramento e valor documental por tipo de dossier</p></div></div>
          <DataTable v-if="!widgetErrors.serviceLines" :value="serviceLineRows" :loading="widgetLoading.serviceLines" :paginator="true" :rows="10" responsiveLayout="scroll">
            <Column field="type" header="Tipo" sortable />
            <Column field="documents" header="Documentos" sortable />
            <Column field="closed" header="Fechados" sortable />
            <Column field="revenue" header="Valor documental" sortable><template #body="slotProps">{{ money(slotProps.data.revenue) }}</template></Column>
          </DataTable>
          <p v-else role="alert">{{ widgetErrors.serviceLines }}</p>
        </section>
      </TabPanel>

      <TabPanel v-if="canOperations" header="Recursos Humanos">
        <div class="reports-kpis reports-kpis--compact">
          <KpiCard label="Utilizadores ativos" :value="number(hr.kpis && hr.kpis.activeUsers)" icon="pi pi-users" />
          <KpiCard label="Técnicos ativos" :value="number(hr.kpis && hr.kpis.activeTechnicians)" icon="pi pi-wrench" tone="blue" />
          <KpiCard label="Ausências" :value="number(hr.kpis && hr.kpis.absences)" icon="pi pi-calendar-times" tone="amber" />
          <KpiCard label="Dias de ausência" :value="number(hr.kpis && hr.kpis.absenceDays)" icon="pi pi-calendar" tone="red" />
        </div>
        <div class="reports-grid reports-grid--thirds">
          <EchartCard title="Equipa por departamento" subtitle="Fotografia atual dos utilizadores ativos" :option="headcountOption" :loading="widgetLoading.hr" :error="widgetErrors.hr" />
          <EchartCard title="Ausências mensais" subtitle="Registos no período" :option="absencesOption" :loading="widgetLoading.hr" :error="widgetErrors.hr" />
          <EchartCard title="Motivos de ausência" subtitle="Distribuição dos registos" :option="absenceTypesOption" :loading="widgetLoading.hr" :error="widgetErrors.hr" />
        </div>
        <section class="reports-note">
          <i class="pi pi-info-circle"></i>
          <div><strong>Disponibilidade de dados RH</strong><p>Os indicadores de faltas serão preenchidos à medida que os registos PHC forem sincronizados.</p></div>
        </section>
      </TabPanel>
    </TabView>
  </div>
</template>

<script>
import TabPanel from "primevue/tabpanel";
import TabView from "primevue/tabview";
import EchartCard from "@/components/dashboards/EchartCard.vue";
import KpiCard from "@/components/dashboards/KpiCard.vue";
import ReportFilterBar from "@/components/dashboards/ReportFilterBar.vue";
import dashboardService from "@/services/dashboard.service";
import {
  categoryChart,
  datasetHorizontalBar,
  gaugeChart,
  mixedCategoryChart,
  pieChart,
} from "@/helpers/echartsOptions";

function defaultFilters() {
  const end = new Date();
  const start = new Date();
  start.setFullYear(start.getFullYear() - 1);
  const format = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return { startDate: format(start), endDate: format(end), serviceType: null, status: null, technicianId: null, brand: null, slaDays: 1 };
}

export default {
  name: "Reports",
  components: { TabPanel, TabView, EchartCard, KpiCard, ReportFilterBar },
  data() {
    return {
      activeTab: 0,
      filters: defaultFilters(),
      filterOptions: { statuses: [], serviceTypes: [], technicians: [], brands: [] },
      loaded: [false, false, false, false, false],
      loading: [false, false, false, false, false],
      widgetLoading: {},
      widgetErrors: {},
      requestVersion: 0,
      error: "",
      summary: {}, pipeline: {}, trend: {}, serviceTypes: {}, brands: {}, sla: {}, clients: {},
      technicians: {}, interventionTypes: {}, parts: {}, stock: {}, equipments: {},
      financialSummary: {}, financial: {}, serviceLines: {}, contracts: {}, hr: {},
    };
  },
  computed: {
    canOperations() { return this.can("seeOperationsDashboard"); },
    canFinance() { return this.can("seeFinanceDashboard"); },
    visibleTabs() { return [0, 1, 2, 3, 4].filter((id) => id === 3 ? this.canFinance : this.canOperations); },
    activeTabId() { return this.visibleTabs[this.activeTab]; },
    filterScope() { return this.activeTabId === 2 ? "parts" : [0, 1].includes(this.activeTabId) ? "operations" : "dates"; },
    currentErrors() {
      return Object.fromEntries(Object.entries(this.widgetErrors).filter(([key]) => this.tabWidgets(this.activeTabId).some(([field]) => field === key)));
    },
    periodLabel() { return `${this.filters.startDate} / ${this.filters.endDate}`; },
    directCosts() {
      return this.financialSummary.partsCost == null || this.financialSummary.laborCost == null
        ? null : Number(this.financialSummary.partsCost) + Number(this.financialSummary.laborCost);
    },
    technicianRows() { return (this.technicians.dataset && this.technicians.dataset.source) || []; },
    clientRows() { return (this.clients.dataset && this.clients.dataset.source) || []; },
    partRows() { return (this.parts.dataset && this.parts.dataset.source) || []; },
    criticalRows() { return (this.stock.criticalItems && this.stock.criticalItems.source) || []; },
    serviceLineRows() { return (this.serviceLines.dataset && this.serviceLines.dataset.source) || []; },
    absenceTypeRows() { return (this.hr.absencesByType && this.hr.absencesByType.items) || []; },
    slaGaugeOption() { return gaugeChart(this.sla.complianceRate, this.sla.slaDays || this.filters.slaDays); },
    pipelineOption() { return categoryChart(this.pipeline, "bar", { horizontal: true }); },
    trendOption() { return categoryChart(this.trend, "line", { zoom: true }); },
    serviceTypesOption() { return pieChart(this.serviceTypes.items, { name: "Serviços" }); },
    brandsOption() { return categoryChart(this.brands, "bar", { stack: true }); },
    clientsOption() { return datasetHorizontalBar(this.clientRows, "client", "pats", "PATs"); },
    slaBucketsOption() { return categoryChart(this.sla.buckets, "bar"); },
    slaMonthlyOption() { return categoryChart(this.sla.monthlyAvgResolution, "line", { valueName: "dias" }); },
    interventionTypesOption() { return pieChart(this.interventionTypes.items, { name: "Intervenções" }); },
    techniciansOption() {
      return categoryChart({
        categories: this.technicianRows.map((row) => row.technician),
        series: [
          { name: "Resolvidos", data: this.technicianRows.map((row) => row.resolved) },
          { name: "Em aberto", data: this.technicianRows.map((row) => row.open) },
        ],
      }, "bar", { horizontal: true });
    },
    partsOption() { return datasetHorizontalBar(this.partRows, "reference", "quantity", "Quantidade"); },
    stockFamiliesOption() { return mixedCategoryChart(this.stock.families, { types: ["bar", "line"], leftName: "EUR", rightName: "Unidades" }); },
    equipmentBrandsOption() { return pieChart(this.equipments.brands && this.equipments.brands.items, { name: "Equipamentos" }); },
    warrantyOption() { return pieChart(this.equipments.warranty && this.equipments.warranty.items, { name: "Garantia" }); },
    financialOption() { return mixedCategoryChart(this.financial, { types: ["bar", "bar", "line"], leftName: "EUR", secondAxis: false, zoom: true }); },
    serviceLinesOption() {
      return pieChart(this.serviceLineRows.filter((row) => row.revenue > 0).map((row) => ({ name: row.type, value: row.revenue })), { name: "Valor documental" });
    },
    contractsOption() { return mixedCategoryChart(this.contracts.monthly, { types: ["bar", "line"], leftName: "Documentos", rightName: "EUR" }); },
    headcountOption() { return pieChart(this.hr.headcountByDepartment && this.hr.headcountByDepartment.items, { name: "Colaboradores" }); },
    absencesOption() { return categoryChart(this.hr.absencesMonthly, "bar"); },
    absenceTypesOption() { return pieChart(this.absenceTypeRows, { name: "Ausências" }); },
  },
  created() {
    this.reportRequests = {};
    if (!this.visibleTabs.length) return;
    dashboardService.getFilterOptions().then((options) => { this.filterOptions = options; }).catch(() => { this.error = "Não foi possível carregar as opções dos filtros."; });
    this.loadTab(this.activeTabId);
  },
  beforeDestroy() { this.requestVersion += 1; },
  methods: {
    number(value) { return value == null ? "-" : new Intl.NumberFormat("pt-PT", { maximumFractionDigits: 1 }).format(Number(value)); },
    money(value) { return value == null ? "-" : new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(value)); },
    percent(value) { return value == null ? "-" : `${this.number(value)}%`; },
    days(value) { return value == null ? "-" : `${this.number(value)} dias`; },
    onTabChange(event) { this.activeTab = event.index; this.loadTab(this.activeTabId); },
    resetFilters() { this.applyFilters(defaultFilters()); },
    applyFilters(filters) {
      this.filters = { ...filters };
      this.requestVersion += 1;
      this.reportRequests = {};
      this.loaded = [false, false, false, false, false];
      this.loading = [false, false, false, false, false];
      this.widgetLoading = {};
      this.widgetErrors = {};
      ["summary", "financialSummary", "pipeline", "trend", "serviceTypes", "brands", "sla", "clients", "technicians", "interventionTypes", "parts", "stock", "equipments", "financial", "serviceLines", "contracts", "hr"].forEach((key) => { this[key] = {}; });
      this.loadTab(this.activeTabId, true);
    },
    tabWidgets(index) {
      const widgets = [
        [["summary", "getSummary", "Resumo operacional"], ["pipeline", "getPatsPipeline", "Pipeline"], ["trend", "getPatsTrend", "Atividade mensal"], ["serviceTypes", "getPatsByServiceType", "Tipos de serviço"], ["brands", "getPatsByBrand", "Marcas"], ["sla", "getSla", "SLA"], ["clients", "getTopClients", "Clientes"]],
        [["sla", "getSla", "SLA"], ["technicians", "getTechnicians", "Técnicos"], ["interventionTypes", "getInterventionTypes", "Intervenções"]],
        [["parts", "getTopParts", "Peças", [this.filters, 15]], ["stock", "getStockHealth", "Stock", [15]], ["equipments", "getEquipments", "Equipamentos", [10]]],
        [["financialSummary", "getFinancialSummary", "Resumo financeiro"], ["financial", "getFinancialMonthly", "Valores mensais"], ["serviceLines", "getServiceLines", "Linhas documentais"], ["contracts", "getContracts", "Contratos"]],
        [["hr", "getHumanResources", "Recursos humanos"]],
      ][index] || [];
      if (index === 0 && this.canFinance) widgets.push(["financialSummary", "getFinancialSummary", "Resumo financeiro"]);
      return widgets;
    },
    loadTab(index, force = false) {
      if (!this.visibleTabs.includes(index) || (this.loaded[index] && !force) || this.loading[index]) return;
      const version = this.requestVersion;
      this.$set(this.loading, index, true);
      const requests = this.tabWidgets(index).map(([key, method, label, args]) => {
        if (this.reportRequests[key]) return this.reportRequests[key];
        if (Object.keys(this[key]).length && !this.widgetErrors[key]) return Promise.resolve(true);
        this[key] = {};
        this.$set(this.widgetLoading, key, true);
        this.$delete(this.widgetErrors, key);
        const params = args || [{ ...this.filters }];
        const promise = Promise.resolve().then(() => dashboardService[method](...params)).then((data) => {
          if (version !== this.requestVersion) return false;
          this[key] = data;
          return true;
        }).catch(() => {
          if (version === this.requestVersion) this.$set(this.widgetErrors, key, `${label}: não foi possível carregar os dados.`);
          return false;
        }).finally(() => {
          if (version === this.requestVersion) {
            this.$set(this.widgetLoading, key, false);
            delete this.reportRequests[key];
          }
        });
        this.reportRequests[key] = promise;
        return promise;
      });
      return Promise.all(requests).then((results) => {
        if (version !== this.requestVersion) return;
        this.$set(this.loaded, index, results.every(Boolean));
        this.$set(this.loading, index, false);
      });
    },
  },
};
</script>

<style scoped>
.reports-page { padding: 0.25rem 0.65rem 2rem; color: #344054; }
.reports-hero { margin-bottom: 1rem; padding: 1.6rem 1.8rem; border-radius: 15px; background: linear-gradient(115deg, #123d45 0%, #0f6f78 62%, #2c8b83 100%); color: #fff; display: flex; justify-content: space-between; align-items: flex-end; box-shadow: 0 12px 30px rgba(15, 79, 87, 0.2); }
.reports-hero__eyebrow { color: #a9ded8; font-size: .7rem; font-weight: 800; letter-spacing: .15em; text-transform: uppercase; }
.reports-hero h1 { margin: .35rem 0; font-size: 2rem; font-weight: 700; }
.reports-hero p { margin: 0; color: #d6efec; }
.reports-hero__period { display: flex; gap: .6rem; align-items: center; border: 1px solid rgba(255,255,255,.24); background: rgba(255,255,255,.1); padding: .65rem .85rem; border-radius: 9px; font-size: .82rem; }
.reports-tabs { margin-top: 1.15rem; }
.reports-tabs ::v-deep .p-tabview-nav { border: 0; background: transparent; gap: .35rem; flex-wrap: wrap; }
.reports-tabs ::v-deep .p-tabview-nav li .p-tabview-nav-link { border: 1px solid #e4e7ec; border-radius: 9px; background: #fff; padding: .85rem 1rem; }
.reports-tabs ::v-deep .p-tabview-nav li.p-highlight .p-tabview-nav-link { color: #0f6f78; border-color: #0f6f78; box-shadow: inset 0 -2px #0f6f78; }
.reports-tabs ::v-deep .p-tabview-panels { padding: 1.1rem 0 0; background: transparent; }
.reports-kpis { display: grid; grid-template-columns: repeat(4, minmax(180px, 1fr)); gap: 1rem; margin-bottom: 1rem; }
.reports-kpis--compact { grid-template-columns: repeat(4, minmax(190px, 1fr)); }
.reports-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; margin-bottom: 1rem; }
.reports-grid--thirds { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.reports-grid--full { grid-template-columns: 1fr; }
.reports-table-card { border: 1px solid #e4e7ec; border-radius: 13px; background: #fff; padding: 1.15rem; box-shadow: 0 5px 18px rgba(16, 24, 40, 0.055); margin-bottom: 1rem; overflow: hidden; }
.reports-table-card--chart-height { min-height: 410px; }
.reports-section-heading { margin-bottom: 1rem; }
.reports-section-heading h3 { margin: 0; color: #1d2939; font-size: 1.05rem; }
.reports-section-heading p { margin: .25rem 0 0; color: #667085; font-size: .8rem; }
.reports-note { padding: 1rem; display: flex; gap: .8rem; border-radius: 10px; background: #edf7f6; border: 1px solid #c5e3df; color: #285e61; }
.reports-note i { font-size: 1.2rem; }
.reports-note p { margin: .25rem 0 0; }
.reports-scope-note { color: #667085; font-size: .8rem; margin: 0 0 1rem; }
@media (max-width: 1250px) {
  .reports-kpis { grid-template-columns: repeat(2, 1fr); }
  .reports-grid--thirds { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 760px) {
  .reports-page { padding: 0 0 1rem; }
  .reports-hero { align-items: flex-start; flex-direction: column; gap: 1rem; padding: 1.25rem; }
  .reports-hero h1 { font-size: 1.55rem; }
  .reports-kpis, .reports-grid, .reports-grid--thirds { grid-template-columns: 1fr; }
}
</style>
